import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import staticArticles from '../data/articles.json';

// Use /api prefix so Vite proxy handles routing (works from any device on network)
const API_BASE = '/api';

interface NewsArticle {
  id: number;
  slug: string;
  title: string;
  summary: string;
  body?: string;
  category: string;
  section?: string;
  sourceName: string | null;
  sourceUrl: string | null;
  publishedAt: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  'tax-law': 'bg-gray-100 text-gray-600',
  'ai-technology': 'bg-gray-100 text-gray-600',
  'industry': 'bg-gray-100 text-gray-600',
  'irs': 'bg-gray-100 text-gray-600',
  'regulation': 'bg-gray-100 text-gray-600',
  'company': 'bg-gray-900 text-white',
};

// Primary nav for /updates. Articles are grouped into one of these sections.
// `category` remains as a finer-grained tag shown on each card.
const SECTION_ORDER = ['filie', 'tax-ai', 'tax-law-irs', 'ai-policy'] as const;
type Section = typeof SECTION_ORDER[number];

const SECTION_LABELS: Record<Section, string> = {
  'filie': 'Filie',
  'tax-ai': 'Tax AI Landscape',
  'tax-law-irs': 'Tax Law & IRS',
  'ai-policy': 'AI & Policy',
};

// Back-compat fallback: if an article came from the API without a `section`
// field, derive it from the legacy `category` so the filter still works.
function inferSection(category: string): Section {
  switch (category) {
    case 'company':       return 'filie';
    case 'irs':
    case 'tax-law':       return 'tax-law-irs';
    case 'regulation':    return 'ai-policy';
    case 'industry':
    case 'ai-technology':
    default:              return 'tax-ai';
  }
}

function ArticleList() {
  const [allArticles, setAllArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState<Section | null>(null);

  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);

      // Try API first (works on local dev with backend running)
      try {
        const articlesRes = await axios.get(`${API_BASE}/news/`);
        if (Array.isArray(articlesRes.data) && articlesRes.data.length > 0) {
          setAllArticles(articlesRes.data);
          setLoading(false);
          return;
        }
      } catch {
        // fall through to static
      }

      // Fall back to bundled static data (the typical production path — API
      // is not deployed, only the React SPA with bundled articles.json).
      setAllArticles(staticArticles as NewsArticle[]);
      setLoading(false);
    };
    fetchArticles();
  }, []);

  // Filter by section client-side. We also derive section for any API-returned
  // article that doesn't carry the field yet (see `inferSection`).
  const articles = section
    ? allArticles.filter((a) => (a.section ?? inferSection(a.category)) === section)
    : allArticles;

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  return (
    <div>
      {/* Section filters — primary nav for /updates */}
      <div className="flex flex-wrap gap-2 mb-8">
        <button
          onClick={() => setSection(null)}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            !section ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 hover:text-gray-800 border border-gray-200'
          }`}
        >
          All
        </button>
        {SECTION_ORDER.map((sec) => (
          <button
            key={sec}
            onClick={() => setSection(sec)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              section === sec ? 'bg-gray-900 text-white' : 'bg-white text-gray-500 hover:text-gray-800 border border-gray-200'
            }`}
          >
            {SECTION_LABELS[sec]}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading updates...</div>
      ) : articles.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500">No updates yet. Check back soon.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {articles.map((article) => (
            <Link
              key={article.id}
              to={`/updates/${article.slug}`}
              className="block border-b border-gray-100 py-6 hover:bg-gray-50 transition-colors -mx-2 px-2 rounded-lg"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${
                        CATEGORY_COLORS[article.category] || 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {article.category.replace(/-/g, ' ')}
                    </span>
                    <span className="text-[11px] text-gray-400">
                      {formatDate(article.publishedAt)}
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1">
                    {article.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{article.summary}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function ArticleDetail({ slug }: { slug: string }) {
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      // Try API first
      try {
        const res = await axios.get(`${API_BASE}/news/${slug}`);
        if (res.data && res.data.slug) {
          setArticle(res.data);
          setLoading(false);
          return;
        }
      } catch {
        // API not available
      }
      // Fall back to static data
      const found = (staticArticles as NewsArticle[]).find((a) => a.slug === slug);
      if (found) setArticle(found);
      setLoading(false);
    };
    fetchArticle();
  }, [slug]);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading...</div>;
  if (!article) return <div className="text-center py-12 text-gray-500">Article not found.</div>;

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  return (
    <div>
      <Link to="/updates" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-800 mb-6">
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back to updates
      </Link>

      <div className="flex items-center gap-2 mb-3">
        <span
          className={`px-2 py-0.5 rounded text-xs font-medium ${
            CATEGORY_COLORS[article.category] || 'bg-gray-100 text-gray-800'
          }`}
        >
          {article.category.replace(/-/g, ' ')}
        </span>
        <span className="text-sm text-gray-400">{formatDate(article.publishedAt)}</span>
      </div>

      <h1 className="text-3xl font-bold text-gray-900 mb-4">{article.title}</h1>
      <p className="text-lg text-gray-600 mb-6">{article.summary}</p>

      <div className="prose prose-gray max-w-none mb-8" dangerouslySetInnerHTML={{ __html: article.body || '' }} />

      {article.sourceUrl && (
        <a
          href={article.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800"
        >
          <ExternalLink className="w-3.5 h-3.5 mr-1" />
          Read original source ({article.sourceName || 'link'})
        </a>
      )}
    </div>
  );
}

export default function News() {
  // Simple client-side routing for article detail
  const path = window.location.pathname;
  const slugMatch = path.match(/^\/updates\/(.+)$/);
  const slug = slugMatch ? slugMatch[1] : null;

  return (
    <div className="bg-white min-h-screen flex flex-col">
      {/* Nav */}
      <nav className="border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <Link to="/">
              <img src="/branding/filie-logo-full.svg" alt="Filie" className="h-7" />
            </Link>
            <div className="flex items-center space-x-6">
              <Link to="/updates" className="text-sm text-gray-500 hover:text-gray-800 transition-colors">
                Updates
              </Link>
              <a
                href="https://x.com/filieai"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-gray-800 transition-colors text-sm"
              >
                Follow @filieai
              </a>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex-1 max-w-3xl mx-auto w-full px-6 lg:px-8 py-16">
        {!slug && (
          <div className="mb-12">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Updates</p>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Tax & AI News</h1>
            <p className="text-gray-500 text-sm">
              Curated news on tax law, AI technology, and the future of tax preparation.
            </p>
          </div>
        )}

        {slug ? <ArticleDetail slug={slug} /> : <ArticleList />}
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 px-6">
        <div className="max-w-3xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <span className="font-semibold text-gray-900 text-sm">Filie</span>
            <span className="text-gray-300 text-sm">&middot;</span>
            <span className="text-gray-400 text-sm">Know your taxes.</span>
          </div>
          <p className="text-xs text-gray-300">© 2026 Filie</p>
        </div>
      </footer>
    </div>
  );
}
